import { gql, useQuery } from '@apollo/client'
import { useMemo } from 'react'

import useBrands from 'pared/layouts/hooks/useBrands'

import { useDateFilter } from '../../dateFilter'
import { useGroupFilter } from '../../groupFilter'
import { IApiDataType } from '../types'

const query = gql`
  query listLocationGroupSystemwideOverview(
    $iStartDate: Date!
    $iEndDate: Date!
    $iFilter: JSON!
    $iInputParams: JSON!
    $useCustom: Boolean!
  ) {
    listLocationGroupFinancialKpis(
      iStartDate: $iStartDate
      iEndDate: $iEndDate
      iFilter: $iFilter
    ) {
      nodes {
        locationGroupId
        netSales
        netSalesBudget
        netSalesBudgetVariance
        netSalesBudgetVarianceAmount
        netSalesSssPercent
        grossSales
        grossSalesBudgetVariance
        grossSalesSssPercent
        hourlyLaborCostPercent
        hourlyLaborBudgetVariance
        cogsPercent
        cogsBudgetVariance
        avtPercent
        rcp
        rcpPercent
        rcpBudgetVariance
      }
    }

    getCustomizedReport(
      iCustomizedReportName: "LIST_LOCATION_GROUP_STORES_CUSTOMIZED_EXTEND_TABLE"
      iInputParams: $iInputParams
    ) @include(if: $useCustom) {
      nodes {
        reportName
        reportDefinition
        reportResult
      }
    }
  }
`

export const listLocationGroupSystemwideOverviewConfigs = {
  netSales: 'price',
  netSalesBudget: 'price',
  netSalesBudgetDiff: 'price',
  netSalesBudgetVariance: 'percent',
  netSalesBudgetVarianceAmount: 'price',
  netSalesSssPercent: 'percent',
  grossSales: 'price',
  grossSalesBudgetVariance: 'percent',
  grossSalesSssPercent: 'percent',
  hourlyLaborCostPercent: 'percent',
  hourlyLaborBudgetVariance: 'percent',
  cogsPercent: 'percent',
  cogsBudgetVariance: 'percent',
  avtPercent: 'percent',
  rcp: 'price',
  rcpPercent: 'percent',
  rcpBudgetVariance: 'percent',

  // customized
  avgWeeklySales: 'price',
  // shared by bbb and mwb
  laborVariance: 'percent',

  // customized bbb
  laborPercent: 'percent',
  fAndPPercent: 'percent',
  fAndPVariance: 'percent',
  ebitdaBeforeBonus: 'price',
  ebitdaBeforeBonusDiff: 'price',
  ebitdaBeforeBonusBudget: 'price',
  ebitdaBeforeBonusPercent: 'percent',
  ebitdaAfterBonusPercent: 'percent',
  grossProfitPercent: 'percent',
  restaurantLevelProfitPercent: 'percent',
  annualizedAvgWeeklySales: 'price',

  // customized mwb
  foodCost: 'percent',
  paperCost: 'percent',
  variableLaborCost: 'percent',
  primeCost: 'percent',
  actualFbPercent: 'percent',
  theoreticalFbPercent: 'percent',
  avtFbPercent: 'percent',
} as const

const USE_CUSTOM = ['bibibop', 'mwb', 'burgerworks', 'dh_wb']

const useListLocationGroupSystemwideOverview = () => {
  const { startDate, endDate } = useDateFilter()
  const { groupFilter, hasGroupBy } = useGroupFilter()
  const { brand } = useBrands()
  const { data, loading } = useQuery(query, {
    variables: {
      iStartDate: startDate,
      iEndDate: endDate,
      iFilter: { location_group_ids: groupFilter?.list?.map((g) => g.id) },
      iInputParams: {
        startDate: startDate,
        endDate: endDate,
        locationGroupIds: groupFilter?.list?.map((g) => g.id),
      },
      useCustom: USE_CUSTOM.includes(brand),
    },
    skip: !startDate || !endDate || !groupFilter || hasGroupBy,
  })

  return {
    data: useMemo((): IApiDataType => {
      const listLocationGroupFinancialKpis = data
        ?.listLocationGroupFinancialKpis.nodes as
        | {
            locationGroupId: number
          }[]
        | undefined
      const customizedReport = data?.getCustomizedReport?.nodes[0]?.reportResult
        ?.tableData as
        | {
            locationGroupId: number
            netSales: number
            netSalesBudget: number
            netSalesBudgetDiff: number
          }[]
        | undefined

      if (!listLocationGroupFinancialKpis) return null

      return {
        source: listLocationGroupFinancialKpis.map((n) => {
          const groupInfo = groupFilter?.list?.find(
            (g) => g.id === n.locationGroupId,
          )
          const customizedData =
            customizedReport?.find(
              (c) => c.locationGroupId === n.locationGroupId,
            ) || {}

          return {
            ...n,
            ...customizedData,
            groupInfo,
          }
        }),
      }
    }, [groupFilter, data]),
    loading,
  }
}

export default useListLocationGroupSystemwideOverview
